import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import JoditEditor from 'jodit-react';
import { Jodit } from 'jodit';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';

interface SafeJoditEditorProps {
    value: string;
    config?: any;
    onBlur?: (newContent: string) => void;
    onChange?: (newContent: string) => void;
    className?: string;
}

const SafeJoditEditor = forwardRef<any, SafeJoditEditorProps>((props, ref) => {
    // Use state to track the editor instance for safe effect dependencies
    const [editor, setEditor] = useState<Jodit | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Keep a mutable ref for the value to access in beforeunload without re-binding
    const valueRef = useRef(props.value);
    const navigate = useNavigate();

    // Expose the editor instance to the parent ref
    useImperativeHandle(ref, () => editor);

    // Update value ref when props change
    useEffect(() => {
        valueRef.current = props.value;
    }, [props.value]);

    // 1. Main Effect: Editor Setup, Events, and Cleanup
    useEffect(() => {
        if (!editor) return; // Wait for editor to be ready

        const handlePopState = () => {
            // Use the captured 'editor' instance from scope
            if (editor && (editor as any).isFullSize) {
                setShowConfirmModal(true);
            }
        };

        const onOpenFullSize = () => {
            window.history.pushState({ joditFullscreen: true }, '');
        };

        // Attach Jodit events
        editor.events.on('afterOpenFullSize', onOpenFullSize);

        // Attach Window events
        window.addEventListener('popstate', handlePopState);

        return () => {
            // Cleanup Window events
            window.removeEventListener('popstate', handlePopState);

            // Cleanup Jodit events (best practice)
            editor.events.off('afterOpenFullSize', onOpenFullSize);

            // 1. Graceful Jodit Exit
            // Use the captured 'editor' variable which is stable in this closure
            if ((editor as any).workplace) {
                try {
                    if ((editor as any).isFullSize) {
                        (editor as any).toggleFullSize(false);
                    }
                } catch (e) { }
            }

            // 2. Force DOM Cleanup (Aggressive Search & Destroy)
            const classesToRemove = ['jodit_fullsize_box', 'jodit_fullsize_box_true', 'jodit_fullsize-box_true'];
            classesToRemove.forEach(className => {
                const affectedElements = document.querySelectorAll(`.${className}`);
                affectedElements.forEach(el => {
                    el.classList.remove(className);
                    if (el.tagName === 'BODY') (el as HTMLElement).style.overflow = '';
                });
            });

            // Also explicitly check html/body just in case
            document.body.classList.remove(...classesToRemove);
            document.documentElement.classList.remove(...classesToRemove);
            document.body.style.overflow = '';

            const fullsizeContainers = document.querySelectorAll('.jodit_fullsize');
            fullsizeContainers.forEach(el => {
                el.classList.remove('jodit_fullsize');
                (el as HTMLElement).style.zIndex = '';
            });
        };
    }, [editor]); // Only re-run if editor instance changes (mounted/unmounted)

    // 2. BeforeUnload Effect (Separate)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (valueRef.current && valueRef.current.trim().length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const handleConfirmExit = () => {
        setShowConfirmModal(false);
        if (editor) {
            try { (editor as any).toggleFullSize(false); } catch (e) { }
        }
        // Force cleanup immediately too
        const classesToRemove = ['jodit_fullsize_box', 'jodit_fullsize_box_true', 'jodit_fullsize-box_true'];
        classesToRemove.forEach(className => {
            const affectedElements = document.querySelectorAll(`.${className}`);
            affectedElements.forEach(el => el.classList.remove(className));
        });
        document.body.style.overflow = '';

        navigate(-1);
    };

    const handleCancelExit = () => {
        setShowConfirmModal(false);
        window.history.pushState({ joditFullscreen: true }, '');
    };

    return (
        <>
            <JoditEditor
                ref={setEditor as any} // Use state setter as ref callback
                value={props.value}
                config={props.config}
                onBlur={props.onBlur}
                onChange={props.onChange}
                className={props.className}
            />

            <Modal
                isOpen={showConfirmModal}
                onClose={handleCancelExit}
                title="Confirm Navigation"
            >
                <div>
                    <p className="text-gray-600 mb-6">
                        You are about to leave the editor. Unsaved changes might be lost. Do you want to go back?
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={handleCancelExit}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            No, Stay
                        </button>
                        <button
                            onClick={handleConfirmExit}
                            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                        >
                            Yes, Go Back
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
});

SafeJoditEditor.displayName = 'SafeJoditEditor';

export default SafeJoditEditor;
